import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLiveRefresh } from '../../context/LiveUpdatesContext';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Plus, Users, Upload, Mail, Archive, RotateCcw, Trash2, Save,
} from 'lucide-react';
import {
  PageHeader, DataTable, Person, StatusPill, Button, Modal, SlideOver, Select,
} from '../../components';
import { agencyService } from '../../services/agencyService';
import {
  inputClass, FieldLabel, FilterTabs, Segmented, ReasonModal, DocList, Field, errorMessage, WEEKDAYS,
  daysLabel, APPROVAL_STATE, stampOf, ActivityChips, punctuality, BookingPill, dayLabel, clockOf, hoursOf,
  localToday, addDays,
} from './shared';
import { BookingDetail } from './AgencyBookings';

/**
 * Agency > Staff — the agency's carers.
 *
 * Adding someone gives them a ServPatch app login (ACH + 6 digits) and emails
 * them a link to set their PIN. One login works at every care home that
 * approves them; each home decides that for itself.
 *
 * Proof (DBS, right to work, training) is what a home approves against.
 * Changing it sends the person back to review at every home that had approved
 * them — confirmed shifts are kept. Contact details and availability don't.
 */

const DayPicker = ({ value, onChange }) => {
  const days = Array.isArray(value) ? value : [];
  const toggle = (d) => onChange(days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort());
  return (
    <div className="flex flex-wrap gap-1.5">
      {WEEKDAYS.map((d) => {
        const on = days.includes(d.value);
        return (
          <button
            key={d.value}
            type="button"
            onClick={() => toggle(d.value)}
            className={`h-9 w-12 rounded-lg text-[12.5px] font-semibold border transition-colors ${on
              ? 'bg-blue-600 border-blue-600 text-white'
              : 'bg-white dark:bg-[#1f232a] border-[#DCE4EF] dark:border-gray-700 text-[#475569] dark:text-gray-300'}`}
          >
            {d.short}
          </button>
        );
      })}
    </div>
  );
};

const AddStaff = ({ open, roles, onClose, onSaved }) => {
  const blank = { name: '', email: '', phone: '', job_role: 'Carer', staff_reference: '', available_days: [1, 2, 3, 4, 5, 6, 7] };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setForm(blank); setError(''); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true); setError('');
    try {
      const res = await agencyService.createStaff(form);
      toast.success(res.message);
      onSaved(res.data?.id);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={busy ? undefined : onClose}
      title="Add staff"
      maxWidth="max-w-lg"
      footer={(
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy}>Add and send login</Button>
        </div>
      )}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <FieldLabel required>Full name</FieldLabel>
          <input className={inputClass} value={form.name} onChange={set('name')} />
        </div>
        <div>
          <FieldLabel required hint="Login + PIN link go here">Email</FieldLabel>
          <input className={inputClass} type="email" value={form.email} onChange={set('email')} />
        </div>
        <div>
          <FieldLabel>Phone</FieldLabel>
          <input className={inputClass} value={form.phone} onChange={set('phone')} />
        </div>
        <div>
          <FieldLabel required>Role</FieldLabel>
          <Select value={form.job_role} onChange={(v) => setForm((f) => ({ ...f, job_role: v }))} options={roles} />
        </div>
        <div>
          <FieldLabel hint="Optional">Your reference</FieldLabel>
          <input className={inputClass} value={form.staff_reference} onChange={set('staff_reference')} />
        </div>
        <div className="sm:col-span-2">
          <FieldLabel>Usually available</FieldLabel>
          <DayPicker value={form.available_days} onChange={(d) => setForm((f) => ({ ...f, available_days: d }))} />
        </div>
      </div>
      <p className="mt-4 text-[12.5px] text-[#64748B] dark:text-gray-400">
        Next, add their DBS, right to work and training under Proof — care homes approve them against it.
      </p>
      {error && <p className="mt-3 text-[12.5px] text-rose-600">{error}</p>}
    </Modal>
  );
};

/** A staff record as the drawer's editable form. */
const formOf = (d) => ({
  name: d?.name || '', email: d?.email || '', phone: d?.phone || '', job_role: d?.job_role, staff_reference: d?.staff_reference || '',
  dbs: { certificate_number: d?.dbs?.certificate_number || '', level: d?.dbs?.level || '', issue_date: d?.dbs?.issue_date || '', review_date: d?.dbs?.review_date || '' },
  rtw: { document_type: d?.rtw?.document_type || '', expiry_date: d?.rtw?.expiry_date || '', no_expiry: Boolean(d?.rtw?.no_expiry) },
  training: (d?.training || []).map((t) => ({ ...t })),
  accepting_offers: d?.accepting_offers,
  available_days: d?.available_days || [],
  unavailable_periods: (d?.unavailable_periods || []).map((p) => ({ ...p })),
});

/**
 * Upload one file into a section of a staff record. With `onPick` it only
 * hands the chosen file back — a course that isn't saved yet holds its
 * certificate until Save training, which uploads it. `beforeUpload` saves
 * what is typed in that tab first; if that fails, nothing is uploaded.
 */
const UploadButton = ({ staffId, section, trainingId, onDone, onPick, beforeUpload, disabled }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (onPick) { onPick(file); return; }
    setBusy(true);
    try {
      const savedFirst = beforeUpload ? await beforeUpload() : false;
      await agencyService.uploadDocument(staffId, { file, section, trainingId });
      toast.success(savedFirst ? 'Details saved and document added' : 'Document added');
      onDone();
    } catch (err) {
      toast.error(errorMessage(err, 'Upload failed.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".pdf,image/*" className="hidden" onChange={pick} />
      <Button size="sm" variant="secondary" leftIcon={<Upload className="w-4 h-4" />} loading={busy} disabled={disabled} onClick={() => ref.current?.click()}>
        Upload
      </Button>
    </>
  );
};

const Section = ({ title, hint, children, action }) => (
  <div className="flex flex-col gap-3 pb-5 border-b border-[#EEF2F6] dark:border-gray-800 last:border-b-0">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[14px] font-bold text-[#0B1220] dark:text-gray-100">{title}</p>
        {hint && <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

/**
 * Work — what this person has worked: hours, punctuality, the homes, and per
 * shift how much they recorded (counts only; the records are the home's).
 */
const WorkTab = ({ staffId }) => {
  const [period, setPeriod] = useState('30');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let live = true;
    setData(null);
    const to = localToday();
    agencyService.staffActivity(staffId, { from: addDays(to, -Number(period)), to })
      .then((res) => { if (live) setData(res.data); })
      .catch((err) => toast.error(errorMessage(err)));
    return () => { live = false; };
  }, [staffId, period]);

  if (!data) return <p className="text-[13px] text-[#94A3B8]">Loading…</p>;
  const st = data.stats;
  const tile = (label, value, tone) => (
    <div className="rounded-xl border border-[#E3E9F3] dark:border-gray-800 px-3 py-2.5">
      <p className="text-[11px] font-semibold text-[#64748B]">{label}</p>
      <p className={`text-[17px] font-bold tabular-nums ${tone === 'warn' ? 'text-amber-600' : 'text-[#0B1220] dark:text-gray-100'}`}>{value}</p>
    </div>
  );

  return (
    <div className="flex flex-col gap-5">
      <Segmented
        size="sm"
        value={period}
        onChange={setPeriod}
        options={[{ value: '7', label: '7 days' }, { value: '30', label: '30 days' }, { value: '90', label: '90 days' }]}
      />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {tile('Shifts worked', `${st.shifts_worked} of ${st.shifts_booked}`)}
        {tile('Hours worked', hoursOf(st.worked_minutes))}
        {tile('Approved', hoursOf(st.approved_minutes))}
        {tile('On time', st.on_time_rate == null ? '—' : `${st.on_time_rate}%`, st.on_time_rate != null && st.on_time_rate < 90 ? 'warn' : null)}
      </div>
      <div className="flex flex-col gap-1.5">
        <p className="text-[12px] font-semibold text-[#334155] dark:text-gray-300">Recorded on their shifts</p>
        <ActivityChips activity={data.activity_totals} />
        <p className="text-[11.5px] text-[#94A3B8]">
          Counts only — the care records belong to the homes.{st.homes?.length ? ` Worked at: ${st.homes.join(', ')}.` : ''}
          {(st.late || st.missed || st.no_show) ? ` ${[st.late && `${st.late} late`, st.missed && `${st.missed} missed`, st.no_show && `${st.no_show} did not attend`].filter(Boolean).join(' · ')}.` : ''}
        </p>
      </div>

      {data.upcoming?.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <p className="text-[12px] font-semibold text-[#334155] dark:text-gray-300">Coming up</p>
          {data.upcoming.map((b) => (
            <button key={b.id} type="button" onClick={() => setOpen(b)} className="flex items-center justify-between gap-2 text-left text-[13px] rounded-lg px-2 py-1.5 hover:bg-[#F5F8FC] dark:hover:bg-gray-800/50">
              <span>{dayLabel(b.shift_date)} · {b.start_time}–{b.end_time} · {b.home?.name}</span>
              <BookingPill booking={b} />
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <p className="text-[12px] font-semibold text-[#334155] dark:text-gray-300">Shift history</p>
        {!data.history.length && <p className="text-[13px] text-[#94A3B8]">No shifts in this period.</p>}
        {data.history.map((b) => (
          <button key={b.id} type="button" onClick={() => setOpen(b)} className="text-left rounded-xl border border-[#E3E9F3] dark:border-gray-800 px-3 py-2.5 hover:shadow-sm transition flex flex-col gap-1">
            <span className="flex items-center justify-between gap-2">
              <span className="text-[13px] font-semibold text-[#0B1220] dark:text-gray-100">{dayLabel(b.shift_date)} · {b.home?.name}</span>
              <BookingPill booking={b} />
            </span>
            <span className="text-[12px] text-[#64748B]">
              Booked {b.start_time}–{b.end_time}
              {b.actual?.clock_in ? ` · clocked ${clockOf(b.actual.clock_in)}–${b.actual.clock_out ? clockOf(b.actual.clock_out) : 'now'} · ${hoursOf(b.actual.worked_minutes)}` : ''}
              {punctuality(b.attendance) ? ` · ${punctuality(b.attendance)}` : ''}
            </span>
            {b.activity && <ActivityChips activity={b.activity} />}
          </button>
        ))}
      </div>
      <BookingDetail booking={open} onClose={() => setOpen(null)} />
    </div>
  );
};

const StaffPanel = ({ staffId, roles, onClose, onChanged, initialTab = 'work' }) => {
  const [s, setS] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // Just the record — documents, issues, status. Leaves the forms alone, so
  // adding a file never wipes edits the user hasn't saved yet.
  const fetchStaff = useCallback(async () => {
    const res = await agencyService.getStaff(staffId);
    setS(res.data);
    return res.data;
  }, [staffId]);

  const load = useCallback(async () => {
    try {
      const d = await fetchStaff();
      setForm(formOf(d));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }, [fetchStaff]);
  useEffect(() => { if (staffId) { setS(null); setTab(initialTab); load(); } }, [staffId, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // After saving some fields: re-read the record and re-sync ONLY those fields,
  // so saving one tab never discards what is typed (unsaved) on another.
  const resync = async (keys) => {
    const fresh = formOf(await fetchStaff());
    setForm((f) => ({ ...f, ...Object.fromEntries(keys.map((k) => [k, fresh[k]])) }));
  };

  const save = async (payload, message = 'Saved') => {
    setBusy(true);
    try {
      const res = await agencyService.updateStaff(staffId, payload);
      toast.success(res.message || message);
      await resync(Object.keys(payload));
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Upload on the Proof tab saves what is typed there FIRST. People fill in the
   * certificate number and dates, then add the scan, and expect both kept —
   * the upload used to store only the file and the typed details were lost.
   * Throws (so the upload doesn't happen) if what's typed can't be saved.
   */
  const saveProofFirst = async () => {
    const typed = { dbs: form.dbs, rtw: form.rtw };
    const saved = formOf(s);
    if (JSON.stringify(typed) === JSON.stringify({ dbs: saved.dbs, rtw: saved.rtw })) return false;
    await agencyService.updateStaff(staffId, typed);
    return true;
  };

  const removeDoc = async (section, doc, trainingId) => {
    try {
      await agencyService.removeDocument(staffId, { section, trainingId, path: doc.path });
      toast.success('Document removed');
      await fetchStaff();
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  /**
   * Save the course list, then upload any certificate picked for a course that
   * wasn't saved yet. The API keeps the order it was sent, so the n-th course
   * saved is the n-th course on screen — that's how a new course finds its id.
   */
  const typedTraining = () => form.training.map(({ documents, pendingFile, ...t }) => ({ ...t, id: String(t.id).startsWith('new') ? '' : t.id }));
  const trainingChanged = () => form.training.some((t) => t.pendingFile)
    || JSON.stringify(typedTraining()) !== JSON.stringify((s?.training || []).map(({ documents, ...t }) => t));

  /** Save the course list + upload held certificates. Throws if the save fails. */
  const persistTraining = async () => {
    const pending = form.training.map((t, i) => [i, t.pendingFile]).filter(([, file]) => file);
    const res = await agencyService.updateStaff(staffId, { training: typedTraining() });
    let failed = 0;
    if (pending.length) {
      const saved = (await agencyService.getStaff(staffId)).data?.training || [];
      for (const [i, file] of pending) {
        const id = saved[i]?.id;
        try {
          if (!id) throw new Error('missing course');
          await agencyService.uploadDocument(staffId, { file, section: 'training', trainingId: id });
        } catch (_) {
          failed += 1;
        }
      }
    }
    await resync(['training']);
    onChanged();
    return { res, failed };
  };

  const saveTraining = async () => {
    setBusy(true);
    try {
      const { res, failed } = await persistTraining();
      if (failed) toast.error(`Training saved, but ${failed === 1 ? 'a certificate' : `${failed} certificates`} didn't upload — add ${failed === 1 ? 'it' : 'them'} again.`);
      else toast.success(res.message || 'Saved');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // Upload on a saved course: save the edited course list first, same reason.
  const saveTrainingFirst = async () => {
    if (!trainingChanged()) return false;
    await persistTraining();
    return true;
  };

  const refresh = async () => { await load(); onChanged(); };
  const refreshDocs = async () => {
    try { await fetchStaff(); } catch (err) { toast.error(errorMessage(err)); }
    onChanged();
  };
  const archived = s?.status === 'archived';
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setIn = (group, k) => (e) => setForm((f) => ({ ...f, [group]: { ...f[group], [k]: e.target.value } }));

  return (
    <SlideOver
      isOpen={Boolean(staffId)}
      onClose={onClose}
      width="sm:max-w-[640px]"
      title={s?.name || 'Staff member'}
      subtitle={s ? `${s.user_id} · ${s.job_role}${archived ? ' · Archived' : ''}` : ''}
    >
      {!s ? <p className="text-[13px] text-[#94A3B8]">Loading…</p> : (
        <div className="flex flex-col gap-5">
          {s.issues?.length > 0 && !archived && (
            <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 px-4 py-3">
              <p className="text-[12.5px] font-bold text-amber-800 dark:text-amber-300 mb-1">Not ready for care homes yet</p>
              <ul className="text-[12.5px] text-amber-800 dark:text-amber-300 list-disc pl-4">
                {s.issues.map((i) => <li key={i}>{i}</li>)}
              </ul>
            </div>
          )}
          <div className="overflow-x-auto">
            <Segmented
              value={tab}
              onChange={setTab}
              size="sm"
              options={[
                { value: 'work', label: 'Work' },
                { value: 'details', label: 'Details' },
                { value: 'proof', label: 'Proof' },
                { value: 'training', label: 'Training' },
                { value: 'availability', label: 'Availability' },
                { value: 'homes', label: 'Care homes' },
              ]}
            />
          </div>

          {tab === 'work' && <WorkTab staffId={staffId} />}

          {tab === 'details' && (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><FieldLabel required>Full name</FieldLabel><input className={inputClass} value={form.name} onChange={set('name')} disabled={archived} /></div>
                <div><FieldLabel required>Email</FieldLabel><input className={inputClass} value={form.email} onChange={set('email')} disabled={archived} /></div>
                <div><FieldLabel>Phone</FieldLabel><input className={inputClass} value={form.phone} onChange={set('phone')} disabled={archived} /></div>
                <div>
                  <FieldLabel required hint="Changing it needs re-approval">Role</FieldLabel>
                  <Select value={form.job_role} onChange={(v) => setForm((f) => ({ ...f, job_role: v }))} options={roles} disabled={archived} />
                </div>
                <div><FieldLabel>Your reference</FieldLabel><input className={inputClass} value={form.staff_reference} onChange={set('staff_reference')} disabled={archived} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4 rounded-xl bg-[#F5F8FC] dark:bg-gray-800/40 p-4">
                <Field label="App login">{s.user_id}</Field>
                <Field label="PIN">{s.setup_complete ? 'Set' : `Not set yet${s.invite_expires_at ? ` · link expires ${stampOf(s.invite_expires_at)}` : ''}`}</Field>
              </div>
              <div className="flex flex-wrap justify-between gap-2">
                <div className="flex gap-2">
                  {archived ? (
                    <Button variant="secondary" leftIcon={<RotateCcw className="w-4 h-4" />} onClick={async () => {
                      try { const res = await agencyService.restoreStaff(staffId); toast.success(res.message); refresh(); } catch (err) { toast.error(errorMessage(err)); }
                    }}
                    >Restore</Button>
                  ) : (
                    <>
                      <Button variant="secondary" leftIcon={<Mail className="w-4 h-4" />} onClick={async () => {
                        try { const res = await agencyService.resendStaffInvite(staffId); toast.success(res.message); refresh(); } catch (err) { toast.error(errorMessage(err)); }
                      }}
                      >{s.setup_complete ? 'Send PIN reset' : 'Resend login email'}</Button>
                      <Button variant="danger-soft" leftIcon={<Archive className="w-4 h-4" />} onClick={() => setArchiving(true)}>Archive</Button>
                    </>
                  )}
                </div>
                {!archived && (
                  <Button leftIcon={<Save className="w-4 h-4" />} loading={busy} onClick={() => save({
                    name: form.name, email: form.email, phone: form.phone, job_role: form.job_role, staff_reference: form.staff_reference,
                  })}
                  >Save details</Button>
                )}
              </div>
            </div>
          )}

          {tab === 'proof' && (
            <div className="flex flex-col gap-5">
              <Section
                title="DBS"
                hint="Certificate and review date. Homes need the certificate on file."
                action={<UploadButton staffId={staffId} section="dbs" beforeUpload={saveProofFirst} onDone={refreshDocs} disabled={archived} />}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><FieldLabel>Certificate number</FieldLabel><input className={inputClass} value={form.dbs.certificate_number} onChange={setIn('dbs', 'certificate_number')} disabled={archived} /></div>
                  <div>
                    <FieldLabel>Level</FieldLabel>
                    <Select
                      value={form.dbs.level}
                      onChange={(v) => setForm((f) => ({ ...f, dbs: { ...f.dbs, level: v } }))}
                      options={[{ value: '', label: 'Not recorded' }, { value: 'basic', label: 'Basic' }, { value: 'standard', label: 'Standard' }, { value: 'enhanced', label: 'Enhanced' }, { value: 'enhanced_barred', label: 'Enhanced with barred lists' }]}
                      disabled={archived}
                    />
                  </div>
                  <div><FieldLabel>Issue date</FieldLabel><input type="date" className={inputClass} value={form.dbs.issue_date} onChange={setIn('dbs', 'issue_date')} disabled={archived} /></div>
                  <div><FieldLabel hint="Optional">Review by</FieldLabel><input type="date" className={inputClass} value={form.dbs.review_date} onChange={setIn('dbs', 'review_date')} disabled={archived} /></div>
                </div>
                <DocList docs={s.dbs?.documents || []} onRemove={archived ? undefined : (d) => removeDoc('dbs', d)} />
              </Section>
              <Section
                title="Right to work"
                hint="An expiry date, or No expiry for a permanent right."
                action={<UploadButton staffId={staffId} section="rtw" beforeUpload={saveProofFirst} onDone={refreshDocs} disabled={archived} />}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><FieldLabel>Document</FieldLabel><input className={inputClass} placeholder="e.g. Passport, Share code" value={form.rtw.document_type} onChange={setIn('rtw', 'document_type')} disabled={archived} /></div>
                  <div>
                    <FieldLabel>Expiry</FieldLabel>
                    <input type="date" className={inputClass} value={form.rtw.expiry_date} disabled={archived || form.rtw.no_expiry} onChange={setIn('rtw', 'expiry_date')} />
                    <label className="mt-1.5 flex items-center gap-2 text-[12.5px] text-[#475569] dark:text-gray-300">
                      <input type="checkbox" checked={form.rtw.no_expiry} disabled={archived} onChange={(e) => setForm((f) => ({ ...f, rtw: { ...f.rtw, no_expiry: e.target.checked, expiry_date: e.target.checked ? '' : f.rtw.expiry_date } }))} />
                      No expiry
                    </label>
                  </div>
                </div>
                <DocList docs={s.rtw?.documents || []} onRemove={archived ? undefined : (d) => removeDoc('rtw', d)} />
              </Section>
              {!archived && (
                <div className="flex justify-end">
                  <Button leftIcon={<Save className="w-4 h-4" />} loading={busy} onClick={() => save({ dbs: form.dbs, rtw: form.rtw })}>Save proof</Button>
                </div>
              )}
            </div>
          )}

          {tab === 'training' && (
            <div className="flex flex-col gap-4">
              <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">Tick Required for training a home must see a certificate for. A certificate added to a new course uploads when you save training.</p>
              {form.training.length === 0 && <p className="text-[13px] text-[#94A3B8]">No training recorded.</p>}
              {form.training.map((t, i) => {
                const saved = (s.training || []).find((x) => String(x.id) === String(t.id));
                const upd = (k, v) => setForm((f) => ({ ...f, training: f.training.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
                return (
                  <div key={t.id || i} className="rounded-xl border border-[#E3E9F3] dark:border-gray-800 p-3 flex flex-col gap-2">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="sm:col-span-3"><input className={inputClass} placeholder="Course name" value={t.name} onChange={(e) => upd('name', e.target.value)} disabled={archived} /></div>
                      <div><FieldLabel>Completed</FieldLabel><input type="date" className={inputClass} value={t.completed_date || ''} onChange={(e) => upd('completed_date', e.target.value)} disabled={archived} /></div>
                      <div><FieldLabel>Expires</FieldLabel><input type="date" className={inputClass} value={t.expiry_date || ''} onChange={(e) => upd('expiry_date', e.target.value)} disabled={archived} /></div>
                      <label className="flex items-center gap-2 text-[12.5px] text-[#475569] dark:text-gray-300 mt-6">
                        <input type="checkbox" checked={Boolean(t.required)} onChange={(e) => upd('required', e.target.checked)} disabled={archived} /> Required
                      </label>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      {t.pendingFile ? (
                        <div className="flex items-center gap-2 text-[13px] min-w-0">
                          <span className="truncate text-[#334155] dark:text-gray-200">{t.pendingFile.name}</span>
                          <span className="shrink-0 text-[11.5px] font-semibold text-amber-600 dark:text-amber-400">Uploads on save</span>
                          <button type="button" onClick={() => upd('pendingFile', null)} className="shrink-0 text-[12px] font-semibold text-rose-600 hover:underline">Remove</button>
                        </div>
                      ) : (
                        <DocList docs={saved?.documents || []} onRemove={archived ? undefined : (d) => removeDoc('training', d, t.id)} />
                      )}
                      {!archived && (
                        <div className="flex gap-1.5 shrink-0">
                          {saved
                            ? <UploadButton staffId={staffId} section="training" trainingId={t.id} beforeUpload={saveTrainingFirst} onDone={refreshDocs} />
                            : <UploadButton onPick={(file) => upd('pendingFile', file)} />}
                          <Button size="sm" variant="ghost" icon={<Trash2 className="w-4 h-4" />} aria-label="Remove course" onClick={() => setForm((f) => ({ ...f, training: f.training.filter((_, j) => j !== i) }))} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {!archived && (
                <div className="flex justify-between">
                  <Button variant="secondary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setForm((f) => ({ ...f, training: [...f.training, { id: `new${Date.now()}`, name: '', completed_date: '', expiry_date: '', required: false }] }))}>Add course</Button>
                  <Button leftIcon={<Save className="w-4 h-4" />} loading={busy} onClick={saveTraining}>Save training</Button>
                </div>
              )}
            </div>
          )}

          {tab === 'availability' && (
            <div className="flex flex-col gap-5">
              <label className="flex items-center justify-between gap-3 rounded-xl border border-[#E3E9F3] dark:border-gray-800 p-4">
                <span>
                  <span className="block text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">Accepting offers</span>
                  <span className="block text-[12.5px] text-[#64748B] dark:text-gray-400">Off = paused. They can't be offered for new shifts.</span>
                </span>
                <input type="checkbox" checked={Boolean(form.accepting_offers)} disabled={archived} onChange={(e) => setForm((f) => ({ ...f, accepting_offers: e.target.checked }))} />
              </label>
              <div>
                <FieldLabel>Usually available</FieldLabel>
                <DayPicker value={form.available_days} onChange={(d) => setForm((f) => ({ ...f, available_days: d }))} />
              </div>
              <div className="flex flex-col gap-2">
                <FieldLabel>Unavailable dates</FieldLabel>
                {form.unavailable_periods.map((p, i) => (
                  <div key={p.id || i} className="grid grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-center">
                    <input type="date" className={inputClass} value={p.from} onChange={(e) => setForm((f) => ({ ...f, unavailable_periods: f.unavailable_periods.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)) }))} />
                    <input type="date" className={inputClass} value={p.to} onChange={(e) => setForm((f) => ({ ...f, unavailable_periods: f.unavailable_periods.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)) }))} />
                    <input className={inputClass} placeholder="Note" value={p.note || ''} onChange={(e) => setForm((f) => ({ ...f, unavailable_periods: f.unavailable_periods.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)) }))} />
                    <Button size="sm" variant="ghost" icon={<Trash2 className="w-4 h-4" />} aria-label="Remove" onClick={() => setForm((f) => ({ ...f, unavailable_periods: f.unavailable_periods.filter((_, j) => j !== i) }))} />
                  </div>
                ))}
                <div>
                  <Button size="sm" variant="secondary" leftIcon={<Plus className="w-4 h-4" />} disabled={archived} onClick={() => setForm((f) => ({ ...f, unavailable_periods: [...f.unavailable_periods, { from: '', to: '', note: '' }] }))}>Add dates</Button>
                </div>
              </div>
              {!archived && (
                <div className="flex justify-end">
                  <Button leftIcon={<Save className="w-4 h-4" />} loading={busy} onClick={() => save({
                    accepting_offers: form.accepting_offers,
                    available_days: form.available_days,
                    unavailable_periods: form.unavailable_periods.filter((p) => p.from || p.to),
                  })}
                  >Save availability</Button>
                </div>
              )}
            </div>
          )}

          {tab === 'homes' && (
            <div className="flex flex-col gap-2">
              {!s.homes?.length && <p className="text-[13px] text-[#94A3B8]">No connected care homes yet. Homes appear here once you accept their request.</p>}
              {(s.homes || []).map((h) => {
                const meta = APPROVAL_STATE[h.state] || APPROVAL_STATE.review;
                return (
                  <div key={h.home_id} className="rounded-xl border border-[#E3E9F3] dark:border-gray-800 p-3 flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13.5px] font-semibold text-[#0B1220] dark:text-gray-100">{h.home_name}</span>
                      <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                    </div>
                    {h.note && <p className="text-[12.5px] text-rose-600">Asked to change: {h.note}</p>}
                    {h.state === 'approved' && (
                      <p className="text-[12px] text-[#64748B] dark:text-gray-400">{h.can_log_medication ? 'Can record medication there' : 'Cannot record medication there'}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <ReasonModal
        open={archiving}
        title={`Archive ${s?.name || ''}`}
        intro="Their outstanding offers are withdrawn and they can no longer sign in. Their history is kept and you can restore them."
        confirmLabel="Archive"
        onClose={() => setArchiving(false)}
        onSubmit={async (reason) => {
          const res = await agencyService.archiveStaff(staffId, reason);
          toast.success(res.message);
          setArchiving(false);
          refresh();
        }}
      />
    </SlideOver>
  );
};

const AgencyStaff = () => {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState('active');
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  // ?open=<id>&tab=<tab> — opened from Hours by staff or the Overview.
  const [openId, setOpenId] = useState(() => Number(params.get('open')) || null);
  const [panelTab] = useState(() => params.get('tab') || 'work');
  useEffect(() => {
    if (params.get('open')) setParams((p) => { const next = new URLSearchParams(p); next.delete('open'); next.delete('tab'); return next; }, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [counts, setCounts] = useState({ active: 0, archived: 0 });

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const [res, other] = await Promise.all([
        agencyService.staff(status),
        agencyService.staff(status === 'active' ? 'archived' : 'active'),
      ]);
      setRows(res.data || []);
      setRoles((res.meta?.job_roles || []).map((r) => ({ value: r, label: r })));
      setCounts(status === 'active'
        ? { active: res.data.length, archived: other.data.length }
        : { active: other.data.length, archived: res.data.length });
    } catch (err) {
      if (!quiet) toast.error(errorMessage(err, 'Could not load staff.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [status]);
  useEffect(() => { load(); }, [load]);
  // The list only — an open staff drawer is an editor and keeps what is typed.
  useLiveRefresh(['agency'], () => load({ quiet: true }));

  const q = search.trim().toLowerCase();
  const list = q ? rows.filter((r) => [r.name, r.user_id, r.email, r.staff_reference].some((v) => String(v || '').toLowerCase().includes(q))) : rows;

  const columns = [
    { key: 'name', header: 'Staff', sortable: true, render: (r) => <Person name={r.name} sub={`${r.user_id}${r.staff_reference ? ` · ${r.staff_reference}` : ''}`} image={r.avatar_url} /> },
    { key: 'job_role', header: 'Role', sortable: true },
    {
      key: 'availability', header: 'Availability',
      render: (r) => (
        <div className="text-[12.5px]">
          {r.accepting_offers ? <span className="text-emerald-700 dark:text-emerald-400 font-semibold">Accepting offers</span> : <span className="text-amber-700 font-semibold">Paused</span>}
          <span className="block text-[#94A3B8]">{daysLabel(r.available_days)}</span>
        </div>
      ),
    },
    {
      key: 'proof', header: 'Proof',
      render: (r) => (r.issues.length ? <StatusPill tone="warn">{r.issues.length} to fix</StatusPill> : <StatusPill tone="good">Complete</StatusPill>),
    },
    {
      key: 'homes', header: 'Care homes',
      render: (r) => {
        const approved = r.homes.filter((h) => h.state === 'approved').length;
        return r.homes.length ? <span className="text-[13px]">{approved} of {r.homes.length} approved</span> : <span className="text-[#94A3B8] text-[13px]">No homes yet</span>;
      },
    },
    { key: 'login', header: 'Login', render: (r) => (r.setup_complete ? <StatusPill tone="good">PIN set</StatusPill> : <StatusPill tone="mute">Invited</StatusPill>) },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Staff"
        count={counts.active}
        subtitle="Your carers. Each gets one app login for every care home that approves them."
        actions={<Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setAdding(true)}>Add staff</Button>}
      />
      <FilterTabs
        value={status}
        onChange={setStatus}
        tabs={[{ value: 'active', label: 'Active', count: counts.active }, { value: 'archived', label: 'Archived', count: counts.archived }]}
      />
      <DataTable
        columns={columns}
        data={list}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search name, login ID or reference…"
        rowKey={(r) => r.id}
        onRowClick={(r) => setOpenId(r.id)}
        emptyIcon={Users}
        emptyTitle={status === 'active' ? 'No staff yet' : 'No archived staff'}
        emptyText={status === 'active' ? 'Add your carers so care homes can approve them and you can offer them for shifts.' : undefined}
      />
      <AddStaff
        open={adding}
        roles={roles.length ? roles : [{ value: 'Carer', label: 'Carer' }]}
        onClose={() => setAdding(false)}
        onSaved={(id) => { setAdding(false); load(); if (id) setOpenId(id); }}
      />
      <StaffPanel staffId={openId} roles={roles} initialTab={panelTab} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
};

export default AgencyStaff;
